<script lang="ts">
	import { writable } from 'svelte/store';
	import { format } from 'svelte-i18n';
	import { getDeviceStatus, getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
import { devices } from 'src/stores/lists/devices';
import { temp_schemes } from 'src/stores/lists/scenes-derived';
	import { showNewLayout } from 'src/stores/toDelete/new_layout';
	import {
		Text,
		ControlsContainer,
		TemperatureWheel,
		Button,
		HeadingWithButton,
		IconButton,
	} from '@Shelly/shelly-design-system';
	import ControlPreview from './ControlPreview.svelte';
	import ThermostatWizard from '../TempSchemeWizard/ThermostatWizard.svelte';
	import { _deepClone, capFirstChar } from '../../../helpers/utilities';
	import { real_id } from '../../../helpers/interface_helpers';
	import { temp_scheme_prototype } from '../TempSchemeWizard/temp_scheme_object';
	import type { ITempSchemeObj } from '../../../types/TemperatureSchemeType';
	import { convertExtTempSensorValue, findDeviceSensorID, findExtTempSensor } from '../helpers/utils';
	import { status_configurator } from 'src/helpers/device_data_helpers';
	import { navigateToRoomThermostats } from 'src/stores/ui/navigate-to';

	let className = '';
	export { className as class };
	export let id;

	// TEMPORARY STORE until added field in user_data store and db
	const userDismissThermostatMsgStore = writable(
		JSON.parse(localStorage.getItem('dismiss_thermostat_msg')) || '',
	);

	userDismissThermostatMsgStore.subscribe((val) =>
		localStorage.setItem('dismiss_thermostat_msg', JSON.stringify(val)),
	);

	let showAddEditModal = false;

	$: thermostat = Object.values($temp_schemes).find((t: any) => {
		return t._meta.room == id && t._enabled;
	});

	$: hasAnyThermostatsInRoom = Object.values($temp_schemes).some((t: any) => t._meta.room == id);

	$: type = thermostat?._meta?.type || '';
	$: thermId = thermostat?.id;
	$: thermostatDefTemp = thermostat?.deftemp || 21;
	$: unit = thermostat?.isinfh ? 'F' : 'C';

	$: temp_scheme_object = !thermostat
		? (_deepClone(temp_scheme_prototype) as ITempSchemeObj)
		: (_deepClone(thermostat) as ITempSchemeObj);

	$: sensorDecId = typeof thermostat === 'object' ? thermostat?.tschem?.r.split(':')[1] : undefined;

	$: deviceSensorId = findDeviceSensorID(sensorDecId, thermostat as ITempSchemeObj);

	$: statusStore = getDeviceStatus(deviceSensorId);
	$: deviceSensorIsOnline = $statusStore?.online;

	$: deviceSensor = $devices[deviceSensorId] || {
		id: deviceSensorId,
		deleted: true,
	};

	$: statusDeviceSensorStore = getDeviceStatus(deviceSensor ? deviceSensor.id : undefined);
	$: statusDeviceSensor = $statusDeviceSensorStore;
	$: settingsDeviceSensorStore = getDeviceSettings(deviceSensor ? deviceSensor.id : undefined);
	$: settingsDeviceSensor = $settingsDeviceSensorStore;

	$: extSensorsStatuses = status_configurator(statusDeviceSensor, deviceSensor, settingsDeviceSensor);

	$: extTempSensor = findExtTempSensor(extSensorsStatuses, thermostat, deviceSensor);

	$: extTempSensorValue = convertExtTempSensorValue(
		extTempSensor?.value || extTempSensor?.sensor?.value,
		unit,
	);

	const navigateToThermostats = () => {
		navigateToRoomThermostats(id);
	};

	const a11yProps = {
		tempWheelAriaLabel: $format('label_temperature', { default: 'Temperature' }),
		thumbAriaLabel: $format('label_temperature_thumb', { default: 'Temperature thumb' }),
		incrementBtnAriaLabel: $format('button_increase_temperature', {
			default: 'Increase temperature',
		}),
		decrementBtnAriaLabel: $format('button_decrease_temperature', {
			default: 'Decrease temperature',
		}),
	};
</script>

{#if thermostat}
	<div class={className}>
		<Text element="h2" class="mb-s">
			{$format('label_active_room_thermostat', {
				default: 'Active Thermostat',
			})}
		</Text>

		<ControlPreview
			id={thermId}
			extSensorValue={extTempSensorValue}
			units={unit}
			{thermostatDefTemp}
			{type}
			showToggleBtn={false}
			showWheelTitle
			sensorIsOnline={Boolean(deviceSensorIsOnline)}
			extSensor={$devices[deviceSensorId] || {
				id: deviceSensorId,
				deleted: true,
			}}
		/>
	</div>
{/if}

{#if !thermostat && !$userDismissThermostatMsgStore}
	<ControlsContainer class={className} color={$showNewLayout ? 'color-1' : undefined}>
		<svelte:fragment slot="first">
			<HeadingWithButton class="mb-xs">
				<Text slot="heading" element="p" class="text-bold" noWrap>
					{$format('no_active_thermostat', {
						default: 'No active thermostat yet',
					})}
				</Text>
				<IconButton
					aria-label={$format('button_close', { default: 'Close' })}
					icon="fas fa-close"
					slot="button"
					variant="secondary"
					on:click={() => {
						userDismissThermostatMsgStore.set(true);
					}}
				/>
			</HeadingWithButton>
			<TemperatureWheel {a11yProps} class="mb-s" type="temp" min={0} valuePrefix={unit} disabled />
			{#if !hasAnyThermostatsInRoom}
				<Button size="m" class="width-sm-full" on:click={() => (showAddEditModal = true)}>
					{$format('button_add_thermostat', {
						default: 'Add thermostat',
					})}
				</Button>
			{:else}
				<Button size="m" class="width-sm-full" on:click={navigateToThermostats}>
					{capFirstChar(
						$format('tab_thermostats', {
							values: { type: $format('Thermostats') },
						}),
					)}
				</Button>
			{/if}
		</svelte:fragment>
	</ControlsContainer>

	<ThermostatWizard bind:id={thermId} bind:showAddEditModal {temp_scheme_object} />
{/if}
